import { Controller } from "@hotwired/stimulus"

// Powers the full-page Contract Workspace: AI panel (chat/review tabs +
// collapse), editable draft text (autosaved to the DB), and the send-for-
// signature flow (draft → out-for-signature, which locks editing).
export default class extends Controller {
  static targets = ["aiPanel", "aiTab", "chatPane", "reviewPane", "tabChat", "tabReview",
                    "sendModal", "draftActions", "sentActions", "status", "lock", "editable", "aiNote",
                    "chatInput", "chatMessages", "gcSigns", "docBody", "saveStatus", "issueCount", "history",
                    "signersList", "signersEmpty", "signerCount", "signerModal", "signerRows", "notifyAll",
                    "signerEmailModal", "signerEmailInput", "signerEmailName", "signerEmailError",
                    "sendSignBox", "sendSignName", "sendTypedPreview", "sendTypedPanel", "sendDrawnPanel",
                    "sendPad", "sendKindTab", "sendSignerRows",
                    "detailsForm", "detailsEditBtn", "valueText", "penaltyText", "valueInput", "penaltyInput", "recallModal",
                    "saveTemplateModal", "saveTemplateName", "saveTemplateBtn",
                    "saveTemplateTrigger", "templateChip", "templateChipName", "templateChipStatus",
                    "docRequirements", "docGateNote", "docsPanel", "undoBtn", "redoBtn", "sendSpotsNote"]
  static values = { askUrl: String, sendUrl: String, saveUrl: String, addSignerUrl: String,
                    removeSignerUrl: String, saveSignersUrl: String, signers: Array, draft: Boolean,
                    remindUrl: String, detailsUrl: String, reopenUrl: String, saveTemplateUrl: String,
                    undoUrl: String, redoUrl: String, updateSignerUrl: String }

  // ── Save as template (Larry 2026-07-31) ───────────────────────────────

  openSaveTemplate() {
    if (this.hasSaveTemplateModalTarget) {
      this.saveTemplateModalTarget.classList.remove("hidden")
      this.saveTemplateNameTarget.focus()
    }
  }

  closeSaveTemplate() {
    if (this.hasSaveTemplateModalTarget) this.saveTemplateModalTarget.classList.add("hidden")
  }

  async saveAsTemplate() {
    const name = this.saveTemplateNameTarget.value.trim()
    if (!name) { this.saveTemplateNameTarget.focus(); return }
    this.saveTemplateBtnTarget.disabled = true
    try {
      const resp = await fetch(this.saveTemplateUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        credentials: "same-origin",
        body: JSON.stringify({ name })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.message || "Couldn't save the template.")
      this.closeSaveTemplate()
      // The contract is now LINKED to that template: swap the button for the
      // template's name — every later edit writes through to it.
      this._showTemplateChip(data.template?.name)
      this._toastMsg(`Template “${data.template.name}” saved — your edits now save into it.`, "notice")
    } catch (err) {
      this._toastMsg(err?.message || "Couldn't save the template.", "alert")
    } finally {
      this.saveTemplateBtnTarget.disabled = false
    }
  }

  // Replace the "Save as template" button with the linked template's name.
  _showTemplateChip(name) {
    if (!name) return
    if (this.hasSaveTemplateTriggerTarget) this.saveTemplateTriggerTarget.classList.add("hidden")
    if (this.hasTemplateChipNameTarget) this.templateChipNameTarget.textContent = name
    if (this.hasTemplateChipTarget) this.templateChipTarget.classList.remove("hidden")
  }

  // Per-edit template save state, shown next to the template's name:
  // "saving" while the write-through is in flight, then "saved ✓".
  _templateStatus(state) {
    if (!this.hasTemplateChipStatusTarget || !this.hasTemplateChipTarget) return
    if (this.templateChipTarget.classList.contains("hidden")) return
    const el = this.templateChipStatusTarget
    clearTimeout(this._tplStatusTimer)
    if (state === "saving") {
      el.textContent = "· saving…"
    } else if (state === "saved") {
      el.textContent = "· saved ✓"
      this._tplStatusTimer = setTimeout(() => { el.textContent = "" }, 2500)
    } else if (state === "error") {
      el.textContent = "· couldn't save"
    } else {
      el.textContent = ""
    }
  }

  _toastMsg(message, type) {
    const flash = document.createElement("div")
    flash.className = `flash-toast-${type}`
    flash.setAttribute("data-controller", "flash")
    flash.setAttribute("data-flash-dismiss-after-value", "5000")
    flash.setAttribute("role", "alert")
    flash.innerHTML = `<span>${message}</span>`
    document.body.appendChild(flash)
  }

  // Show a pending flash after a reload (e.g. after sending for signature).
  connect() {
    this._showPendingFlash()
    // Restore the AI chat thread + review (incl. resolved items) saved in this
    // browser for this contract, so they survive a refresh — then auto-persist
    // them on any change.
    this._restorePanels()
    this._panelObserver = new MutationObserver(() => {
      clearTimeout(this._persistTimer)
      this._persistTimer = setTimeout(() => this._persistPanels(), 300)
    })
    if (this.hasChatMessagesTarget) this._panelObserver.observe(this.chatMessagesTarget, { childList: true, subtree: true })
    if (this.hasReviewPaneTarget) this._panelObserver.observe(this.reviewPaneTarget, { childList: true, subtree: true })
    // Never lose an unsaved edit on refresh/close/tab-switch: flush a keepalive
    // save the moment the page is hidden or unloading.
    this._flushBound = this._flushOnHide.bind(this)
    window.addEventListener("pagehide", this._flushBound)
    document.addEventListener("visibilitychange", this._flushBound)
  }

  disconnect() {
    if (this._panelObserver) this._panelObserver.disconnect()
    window.removeEventListener("pagehide", this._flushBound)
    document.removeEventListener("visibilitychange", this._flushBound)
  }

  // ── AI panel collapse ──
  collapseAi() { this.aiPanelTarget.classList.add("hidden"); this.aiTabTarget.classList.remove("hidden") }
  expandAi() { this.aiPanelTarget.classList.remove("hidden"); this.aiTabTarget.classList.add("hidden") }

  // ── AI tabs: Chat | Review ──
  aiTab(e) {
    // In production the Review tab (demo data) isn't rendered — chat only.
    if (!this.hasReviewPaneTarget || !this.hasTabReviewTarget) return
    const chat = e.currentTarget.dataset.tab === "chat"
    this.chatPaneTarget.classList.toggle("hidden", !chat)
    this.chatPaneTarget.classList.toggle("flex", chat)
    this.reviewPaneTarget.classList.toggle("hidden", chat)
    this._tabStyle(this.tabChatTarget, chat)
    this._tabStyle(this.tabReviewTarget, !chat)
  }
  _tabStyle(el, on) {
    el.classList.toggle("text-purple-700", on)
    el.classList.toggle("border-purple-600", on)
    el.classList.toggle("font-medium", on)
    el.classList.toggle("text-gray-500", !on)
    el.classList.toggle("border-transparent", !on)
  }

  // One-tap question chips over an uploaded contract's chat (Phelipe
  // 2026-10-23) — same questions as the received page.
  suggest(e) {
    if (e) e.preventDefault()
    if (!this.hasChatInputTarget) return
    this.chatInputTarget.value = e.currentTarget.textContent.trim()
    this.askAi()
  }

  // ── Ask AI (real Gemini via the `ask` endpoint) ──
  askAi(e) {
    if (e) e.preventDefault()
    const input = this.chatInputTarget
    const question = input.value.trim()
    if (!question || this._busy) return

    this._appendUser(question)
    input.value = ""
    this._busy = true
    const thinking = this._appendThinking()

    // Send the live document text (drafts only) so the AI can edit exactly what
    // the user sees, including any unsaved edits.
    const payload = { question }
    if (this.draftValue && this.hasDocBodyTarget) payload.body = this._cleanBodyHtml()

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.askUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", "Accept": "application/json" },
      body: JSON.stringify(payload)
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        thinking.remove()
        // The AI rewrote the contract — swap the new text in (already saved
        // server-side) and reveal just the section(s) it changed, in place.
        let edits = 0
        if (data.updated_body && this.hasDocBodyTarget) {
          edits = this._applyAiEdit(data.updated_body)
          if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = edits ? "AI edited ✓" : "Saved ✓"
          // The AI edit is persisted server-side; reflect the template
          // write-through in the chip so it reads the same as a hand edit.
          if (data.template) { this._showTemplateChip(data.template.name); this._templateStatus("saved") }
          // An AI edit (or an AI undo) moves where we stand in the wording.
          if (data.can_undo !== undefined) this._setUndoState(data.can_undo, data.can_redo)
        }
        this._appendAi(data.answer || "Sorry, I couldn't answer that.")
        if (edits) this._appendEditNote(edits)
      })
      .catch(() => { thinking.remove(); this._appendAi("Sorry — I couldn't reach the AI just now. Please try again.") })
      .finally(() => { this._busy = false; input.focus() })
  }

  // Apply an AI revision in place: swap in the new text, then highlight just the
  // words/phrases that actually changed (inline, highlighter-style) and tag each
  // edited block with "✦ edited by AI" — scrolling to the first change. A single
  // changed block also types itself in for a live feel. Returns the changed count.
  _applyAiEdit(newHtml) {
    this._ensureEditStyles()
    const container = this.docBodyTarget
    const norm = (s) => (s || "").replace(/\s+/g, " ").trim()

    // Old blocks (cleaned of any prior AI markup) to diff the new ones against.
    const oldByIndex = Array.from(this._blocksRoot(container).children).map((el) => this._blockText(el))
    const oldSet = new Set(oldByIndex.map(norm))

    const probe = document.createElement("div")
    probe.innerHTML = newHtml
    if (!this._blocksRoot(probe).children.length) { container.innerHTML = newHtml; this._flashDoc(); return 0 }

    container.innerHTML = newHtml
    const blocks = Array.from(this._blocksRoot(container).children)
    const changed = []
    blocks.forEach((block, i) => {
      if (oldSet.has(norm(block.textContent))) return // unchanged clause
      const oldText = i < oldByIndex.length ? oldByIndex[i] : ""
      this._highlightChanges(block, oldText)
      changed.push(block)
    })
    if (!changed.length) { this._flashDoc(); return 0 }

    // Later iterations kept landing without the auto-scroll (Phelipe
    // 2026-07-31): the smooth scroll fired in the same frame as the container
    // rebuild and the browser's scroll anchoring swallowed it. Defer a beat.
    //
    // Scroll to where the MOST text actually changed (Phelipe 2026-07-24).
    // Picking the first changed block broke down after a couple of rounds:
    // once the AI renumbers clauses or nudges punctuation, early blocks count
    // as "changed" over a word or two and the view parked at the top instead
    // of on the real edit. Score each block by how much text is inside its
    // <mark> highlights — the genuinely rewritten clause wins, and a brand-new
    // appended section (every word marked) naturally scores highest.
    const markedLength = (b) =>
      Array.from(b.querySelectorAll("mark")).reduce((sum, m) => sum + (m.textContent || "").trim().length, 0)
    let target = changed[0], best = -1
    changed.forEach((b) => {
      const score = markedLength(b)
      if (score > best) { best = score; target = b }
    })
    // Land on the first highlighted phrase inside that block rather than the
    // block's midpoint — a long clause centered by its middle can still leave
    // the actual edit off-screen.
    const focus = target.querySelector("mark") || target
    // Plain timeout, not requestAnimationFrame: rAF is paused while the tab
    // isn't painting (measured — it never fired), which silently skipped the
    // scroll entirely. A timeout always runs and still lets layout settle.
    setTimeout(() => this._scrollDocTo(focus), 80)

    // One changed block → type it in, then drop the tag; many → reveal + tag now.
    clearTimeout(this._twTimer)
    if (changed.length === 1 && norm(changed[0].textContent).length <= 600) {
      this._typewriterBlock(changed[0], () => this._tagBlock(changed[0]))
    } else {
      changed.forEach((el, i) => { el.style.animationDelay = `${i * 120}ms`; el.classList.add("ai-edit-reveal"); this._tagBlock(el) })
    }
    return changed.length
  }

  // The element whose children are the real clauses. AI bodies usually arrive
  // wrapped in a single <div>, which used to make the whole contract count as
  // ONE block: the diff always said "1 section" and the auto-scroll centered
  // the entire 20,000px document instead of the edit (Phelipe 2026-07-24).
  // Descend through single-element wrappers to reach the actual clause list.
  _blocksRoot(el) {
    let root = el
    for (let depth = 0; depth < 4; depth++) {
      const kids = Array.from(root.children)
      if (kids.length === 1 && kids[0].children.length > 1) root = kids[0]
      else break
    }
    return root
  }

  // Text of a block, ignoring any "edited by AI" tag we may have appended.
  _blockText(el) {
    if (!el.querySelector || !el.querySelector(".ai-edit-tag")) return el.textContent
    const c = el.cloneNode(true)
    c.querySelectorAll(".ai-edit-tag").forEach((n) => n.remove())
    return c.textContent
  }

  // Wrap the words added/changed (vs oldText) in <mark> highlights, merging
  // adjacent ones into phrases. Preserves inline tags. Returns the mark count.
  _highlightChanges(block, oldText) {
    const split = (s) => s.split(/(\s+)/)
    const isWord = (t) => t.length > 0 && t.trim().length > 0
    const wordsOf = (s) => split(s).filter(isWord)
    const newWords = wordsOf(block.textContent)
    if (!newWords.length) return 0
    const kept = this._lcsKept(wordsOf(oldText), newWords)

    const textNodes = []
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT, null)
    let tn
    while ((tn = walker.nextNode())) textNodes.push(tn)

    let wi = 0
    let marks = 0
    textNodes.forEach((node) => {
      const parts = split(node.nodeValue)
      let anyAdded = false
      const flags = parts.map((p) => {
        if (!isWord(p)) return { p, ws: true, add: false }
        const add = !kept[wi]; wi++
        if (add) anyAdded = true
        return { p, ws: false, add }
      })
      if (!anyAdded) return
      const frag = document.createDocumentFragment()
      let i = 0
      while (i < flags.length) {
        if (flags[i].add) {
          let run = ""
          while (i < flags.length && (flags[i].add || (flags[i].ws && i + 1 < flags.length && flags[i + 1].add))) {
            run += flags[i].p; i++
          }
          const mk = document.createElement("mark")
          mk.className = "ai-mark"
          mk.textContent = run
          frag.appendChild(mk)
          marks++
        } else {
          frag.appendChild(document.createTextNode(flags[i].p)); i++
        }
      }
      node.parentNode.replaceChild(frag, node)
    })
    return marks
  }

  // Longest-common-subsequence: boolean[] over b — true where b[j] is unchanged,
  // false where it was added/changed.
  _lcsKept(a, b) {
    const m = b.length
    if (!a.length) return new Array(m).fill(false)
    const n = a.length
    const dp = []
    for (let i = 0; i <= n; i++) dp.push(new Uint16Array(m + 1))
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
      }
    }
    const kept = new Array(m).fill(false)
    let i = 0, j = 0
    while (i < n && j < m) {
      if (a[i] === b[j]) { kept[j] = true; i++; j++ }
      else if (dp[i + 1][j] >= dp[i][j + 1]) i++
      else j++
    }
    return kept
  }

  // Append the inline "✦ edited by AI" tag to a changed block.
  _tagBlock(block) {
    if (block.querySelector(":scope > .ai-edit-tag")) return
    const tag = document.createElement("span")
    tag.className = "ai-edit-tag"
    tag.setAttribute("contenteditable", "false")
    tag.innerHTML = `${this._sparklesSvg()} edited by AI`
    block.appendChild(document.createTextNode(" "))
    block.appendChild(tag)
  }

  _sparklesSvg() {
    return '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M15.98 1.804a1 1 0 0 0-1.96 0l-.24 1.192a1 1 0 0 1-.784.785l-1.192.238a1 1 0 0 0 0 1.962l1.192.238a1 1 0 0 1 .785.785l.238 1.192a1 1 0 0 0 1.962 0l.238-1.192a1 1 0 0 1 .785-.785l1.192-.238a1 1 0 0 0 0-1.962l-1.192-.238a1 1 0 0 1-.785-.785l-.238-1.192ZM6.949 5.684a1 1 0 0 0-1.898 0l-.683 2.051a1 1 0 0 1-.633.633l-2.051.683a1 1 0 0 0 0 1.898l2.051.684a1 1 0 0 1 .633.632l.683 2.051a1 1 0 0 0 1.898 0l.683-2.051a1 1 0 0 1 .633-.632l2.051-.684a1 1 0 0 0 0-1.898l-2.051-.683a1 1 0 0 1-.633-.633L6.95 5.684ZM13.949 13.684a1 1 0 0 0-1.898 0l-.184.551a1 1 0 0 1-.632.633l-.551.183a1 1 0 0 0 0 1.898l.551.184a1 1 0 0 1 .632.632l.184.551a1 1 0 0 0 1.898 0l.184-.551a1 1 0 0 1 .633-.632l.551-.184a1 1 0 0 0 0-1.898l-.551-.183a1 1 0 0 1-.633-.633l-.183-.551Z"/></svg>'
  }

  // Document HTML with all AI markup stripped — what we persist to the DB.
  _cleanBodyHtml() {
    const clone = this.docBodyTarget.cloneNode(true)
    clone.querySelectorAll(".ai-edit-tag").forEach((n) => n.remove())
    clone.querySelectorAll("mark").forEach((m) => {
      const parent = m.parentNode
      while (m.firstChild) parent.insertBefore(m.firstChild, m)
      parent.removeChild(m)
    })
    clone.normalize()
    return clone.innerHTML
  }

  // Reveal a block's text character-by-character without touching its markup
  // (mutates only text nodes, so inline tags like <strong> and <mark> stay
  // intact and the highlight appears as the text is written in).
  _typewriterBlock(el, done) {
    // Reserve the block's final height so it doesn't collapse while empty —
    // keeps the layout steady and the auto-scroll centered.
    const reserved = el.getBoundingClientRect().height
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => node.parentElement && node.parentElement.closest(".ai-edit-tag")
        ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
    })
    const nodes = []
    let total = 0
    let node
    while ((node = walker.nextNode())) {
      nodes.push({ node, full: node.nodeValue })
      total += node.nodeValue.length
      node.nodeValue = ""
    }
    if (!total) { if (done) done(); return }
    el.style.minHeight = `${reserved}px`
    const per = Math.max(4, Math.ceil(total / 28))
    let shown = 0
    const step = () => {
      shown += per
      let left = shown
      for (const it of nodes) {
        it.node.nodeValue = left <= 0 ? "" : it.full.slice(0, left)
        left -= it.full.length
      }
      if (shown < total) { this._twTimer = setTimeout(step, 18) }
      else { nodes.forEach((it) => { it.node.nodeValue = it.full }); el.style.minHeight = ""; if (done) done() }
    }
    step()
  }

  // Center the changed text inside the scrolling document pane.
  //
  // The smooth scroll alone was unreliable (Phelipe 2026-07-24): measured on
  // the real workspace, a smooth scrollTo left scrollTop at 0 while the very
  // same computed offset applied instantly landed exactly on the change —
  // the animation gets cancelled by the surrounding DOM swap, the reveal
  // animations, and the contenteditable regaining focus. So: ask for smooth,
  // then VERIFY and snap if it didn't take. Recomputing each pass also
  // absorbs the layout shift from the reveal/typewriter animations.
  _scrollDocTo(el) {
    const scroller = this.docBodyTarget.closest(".overflow-y-auto")
    if (!scroller) { el.scrollIntoView({ block: "center" }); return }

    const targetTop = () => {
      const cRect = scroller.getBoundingClientRect()
      const eRect = el.getBoundingClientRect()
      const raw = scroller.scrollTop + (eRect.top - cRect.top) - (scroller.clientHeight / 2 - eRect.height / 2)
      return Math.max(0, Math.min(raw, scroller.scrollHeight - scroller.clientHeight))
    }

    scroller.scrollTo({ top: targetTop(), behavior: "smooth" })
    // Re-assert after the animations settle; snap instantly if the smooth
    // scroll was swallowed or the content moved under it.
    clearTimeout(this._scrollFixTimer)
    let tries = 0
    const settle = () => {
      const want = targetTop()
      if (Math.abs(scroller.scrollTop - want) > 8) scroller.scrollTop = want
      if (++tries < 3) this._scrollFixTimer = setTimeout(settle, 220)
    }
    this._scrollFixTimer = setTimeout(settle, 260)
  }

  // Small caption under the AI reply pointing at the highlighted change.
  _appendEditNote(n) {
    const note = document.createElement("div")
    note.className = "flex gap-2 pl-8"
    note.innerHTML = `<span class="text-[11px] text-amber-600">✦ Updated ${n} section${n > 1 ? "s" : ""} — highlighted in the contract</span>`
    this.chatMessagesTarget.appendChild(note)
    this._scrollChat()
  }

  // Inject the highlight / reveal styles once.
  _ensureEditStyles() {
    if (document.getElementById("contract-ai-edit-styles")) return
    const style = document.createElement("style")
    style.id = "contract-ai-edit-styles"
    style.textContent =
      "mark.ai-mark{background-color:#fde68a;color:inherit;border-radius:.18rem;padding:.04em .14em;box-decoration-break:clone;-webkit-box-decoration-break:clone;animation:aiMarkIn .45s ease both}" +
      "@keyframes aiMarkIn{from{background-color:#fff7cc}to{background-color:#fde68a}}" +
      ".ai-edit-reveal{animation:aiReveal .45s ease both}" +
      "@keyframes aiReveal{from{opacity:.25;transform:translateY(2px)}to{opacity:1;transform:none}}" +
      ".ai-edit-tag{display:inline-flex;align-items:center;gap:3px;margin-left:6px;font-size:11px;font-weight:600;color:#b45309;vertical-align:baseline;white-space:nowrap;user-select:none;animation:aiReveal .4s ease both}" +
      ".ai-edit-tag svg{width:13px;height:13px;color:#f59e0b;flex:none}"
    document.head.appendChild(style)
  }

  // Fallback: brief whole-document highlight when we can't pinpoint the change.
  _flashDoc() {
    const el = this.docBodyTarget
    el.style.transition = "background-color .4s ease"
    el.style.backgroundColor = "rgba(250, 204, 21, 0.18)"
    setTimeout(() => { el.style.backgroundColor = "" }, 1300)
  }

  // ── "Fix with AI" on a review item ──
  // Sends the item's predefined instruction through the same edit path as chat,
  // swaps in the revised contract, and marks the item resolved.
  fixIssue(e) {
    if (e) e.preventDefault()
    const btn = e.currentTarget
    const instruction = btn.dataset.fix
    if (!instruction || this._busy) return
    if (!this.draftValue) { this._toast("Editing is locked once a contract is sent for signature.", "error"); return }

    this._busy = true
    const original = btn.innerHTML
    btn.disabled = true
    btn.style.opacity = "0.6"
    btn.textContent = "Fixing…"
    const restore = () => { btn.disabled = false; btn.style.opacity = ""; btn.innerHTML = original }

    const payload = { question: instruction }
    if (this.hasDocBodyTarget) payload.body = this._cleanBodyHtml()

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.askUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", "Accept": "application/json" },
      body: JSON.stringify(payload)
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        if (data.updated_body && this.hasDocBodyTarget) {
          const edits = this._applyAiEdit(data.updated_body)
          if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = edits ? "AI edited ✓" : "Saved ✓"
          this._markFixed(btn)
          this._appendAi(data.answer || "Done — I updated the contract.")
          if (edits) this._appendEditNote(edits)
          this._toast("Contract updated by AI")
        } else {
          // AI didn't change anything (e.g. it judged no fix was needed).
          restore()
          this._toast(data.answer || "No change was needed.")
        }
      })
      .catch(() => { restore(); this._toast("Couldn't apply that fix — please try again.", "error") })
      .finally(() => { this._busy = false })
  }

  // Mark a resolved review item: swap its button for a "Fixed" badge, dim the
  // card, and decrement the "things to check" counters.
  _markFixed(btn) {
    const action = btn.closest("[data-issue-action]")
    if (action) action.innerHTML = `<span class="inline-flex items-center gap-1 text-[13px] font-medium text-green-700">✓ Fixed by AI</span>`
    const card = btn.closest("[data-issue-card]")
    if (card) card.style.opacity = "0.65"
    this.issueCountTargets.forEach((el) => {
      const n = parseInt(el.textContent, 10)
      if (!isNaN(n) && n > 0) el.textContent = String(n - 1)
    })
  }

  _appendUser(text) {
    const row = document.createElement("div")
    row.className = "flex gap-2 justify-end"
    const bubble = document.createElement("div")
    bubble.className = "bg-purple-600 text-white rounded-lg rounded-tr-none px-3 py-2 text-[12px] max-w-[230px] whitespace-pre-wrap"
    bubble.textContent = text
    row.appendChild(bubble)
    this.chatMessagesTarget.appendChild(row)
    this._scrollChat()
  }

  _appendAi(text) {
    const row = document.createElement("div")
    row.className = "flex gap-2"
    const avatar = document.createElement("div")
    avatar.className = "w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[11px] shrink-0"
    avatar.textContent = "AI"
    const bubble = document.createElement("div")
    bubble.className = "bg-gray-100 rounded-lg rounded-tl-none px-3 py-2 text-[12px] text-gray-700 whitespace-pre-wrap"
    bubble.textContent = text
    row.appendChild(avatar); row.appendChild(bubble)
    this.chatMessagesTarget.appendChild(row)
    this._scrollChat()
  }

  _appendThinking() {
    const row = document.createElement("div")
    row.className = "flex gap-2"
    row.innerHTML = `<div class="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[11px] shrink-0">AI</div>
      <div class="bg-gray-100 rounded-lg rounded-tl-none px-3 py-2 text-[12px] text-gray-400 italic">Thinking…</div>`
    this.chatMessagesTarget.appendChild(row)
    this._scrollChat()
    return row
  }

  _scrollChat() { this.chatMessagesTarget.scrollTop = this.chatMessagesTarget.scrollHeight }

  // ── Sign & Send for Signature(s) ──
  // The sender adopts a real signature in the modal (typed or drawn — same
  // capture as the public signing page) and can add/fix signers inline
  // before anything goes out (Larry 2026-08-05).
  openSend(e) {
    if (e) e.preventDefault()
    // Fresh signer rows on every open: the saved list, else the counterparty.
    if (this.hasSendSignerRowsTarget) {
      const fallback = { name: this.sendSignerRowsTarget.dataset.counterparty || "",
                         role: this.sendSignerRowsTarget.dataset.counterpartyRole || "Subcontractor" }
      const current = (this.signersValue && this.signersValue.length) ? this.signersValue : [fallback]
      this.sendSignerRowsTarget.innerHTML = ""
      current.forEach((s) => this.sendSignerRowsTarget.appendChild(this._buildModalRow(s)))
      this._renumberModal()
    }
    if (!this._sendPadReady && this.hasSendPadTarget) { this._initSendPad(); this._sendPadReady = true }
    // Uploaded PDF: say where the signatures will land (or that none were placed).
    const spots = this._spots()
    if (spots && this.hasSendSpotsNoteTarget) {
      const { text, warn } = spots.summaryText()
      this.sendSpotsNoteTarget.textContent = text
      this.sendSpotsNoteTarget.className = `rounded-lg px-3 py-2 text-[13px] ${warn ? "bg-amber-50 text-amber-800" : "bg-gray-50 text-gray-600"}`
    }
    this._refreshDocGateNote()
    this.refreshSendPreview()
    this.sendModalTarget.classList.remove("hidden")
  }
  closeSend() { this.sendModalTarget.classList.add("hidden") }
  backdropClose(e) { if (e.target === e.currentTarget) this.closeSend() }

  toggleSendSign() {
    const on = this.hasGcSignsTarget ? this.gcSignsTarget.checked : true
    if (this.hasSendSignBoxTarget) this.sendSignBoxTarget.classList.toggle("hidden", !on)
  }

  pickSendKind(e) {
    this._sendKind = e.currentTarget.dataset.kind
    this.sendKindTabTargets.forEach((tab) => {
      const on = tab.dataset.kind === this._sendKind
      tab.classList.toggle("bg-white", on)
      tab.classList.toggle("shadow-sm", on)
      tab.classList.toggle("font-medium", on)
      tab.classList.toggle("text-gray-800", on)
      tab.classList.toggle("text-gray-600", !on)
    })
    if (this.hasSendTypedPanelTarget) this.sendTypedPanelTarget.classList.toggle("hidden", this._sendKind !== "typed")
    if (this.hasSendDrawnPanelTarget) this.sendDrawnPanelTarget.classList.toggle("hidden", this._sendKind !== "drawn")
  }

  refreshSendPreview() {
    if (this.hasSendTypedPreviewTarget && this.hasSendSignNameTarget) {
      this.sendTypedPreviewTarget.textContent = this.sendSignNameTarget.value.trim() || "Your name"
    }
  }

  // Same pad mechanics as the public signing page (contract_sign_controller).
  _initSendPad() {
    const canvas = this.sendPadTarget
    const ctx = canvas.getContext("2d")
    ctx.lineWidth = 2.2
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.strokeStyle = "#1f2937"
    let drawing = false
    const pos = (e) => {
      const r = canvas.getBoundingClientRect()
      return [(e.clientX - r.left) * (canvas.width / r.width),
              (e.clientY - r.top) * (canvas.height / r.height)]
    }
    canvas.addEventListener("pointerdown", (e) => {
      e.preventDefault()
      drawing = true
      canvas.setPointerCapture(e.pointerId)
      ctx.beginPath()
      ctx.moveTo(...pos(e))
    })
    canvas.addEventListener("pointermove", (e) => {
      if (!drawing) return
      e.preventDefault()
      ctx.lineTo(...pos(e))
      ctx.stroke()
      this._sendPadDirty = true
    })
    const stop = () => { drawing = false }
    canvas.addEventListener("pointerup", stop)
    canvas.addEventListener("pointercancel", stop)
  }

  clearSendPad() {
    if (!this.hasSendPadTarget) return
    const ctx = this.sendPadTarget.getContext("2d")
    ctx.clearRect(0, 0, this.sendPadTarget.width, this.sendPadTarget.height)
    this._sendPadDirty = false
  }

  addSendSignerRow(e) {
    if (e) e.preventDefault()
    const row = this._buildModalRow({ role: this.hasSendSignerRowsTarget ? (this.sendSignerRowsTarget.dataset.counterpartyRole || "") : "" })
    this.sendSignerRowsTarget.appendChild(row)
    this._renumberModal()
    const input = row.querySelector("[data-name]")
    if (input) input.focus()
  }

  // ── History Log slide-over ──
  openHistory(e) { if (e) e.preventDefault(); this.historyTarget.classList.remove("hidden"); document.body.style.overflow = "hidden" }
  closeHistory() { this.historyTarget.classList.add("hidden"); document.body.style.overflow = "" }

  // ── Undo / Redo the draft's wording (Larry 2026-09-15) ──
  undoBody(e) { this._stepBody(e, this.hasUndoUrlValue ? this.undoUrlValue : null) }
  redoBody(e) { this._stepBody(e, this.hasRedoUrlValue ? this.redoUrlValue : null) }

  async _stepBody(e, url) {
    if (e) e.preventDefault()
    if (!url || this._stepping) return
    this._stepping = true
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(url, { method: "POST", credentials: "same-origin",
        headers: { "X-CSRF-Token": token || "", "Accept": "application/json" } })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Nothing to undo"
        return
      }
      if (this.hasDocBodyTarget && typeof data.body === "string") this.docBodyTarget.innerHTML = data.body
      if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Saved ✓"
      this._setUndoState(data.can_undo, data.can_redo)
    } catch (_) {
      if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Couldn't do that — try again"
    } finally {
      this._stepping = false
    }
  }

  _setUndoState(canUndo, canRedo) {
    if (this.hasUndoBtnTarget) this.undoBtnTarget.disabled = !canUndo
    if (this.hasRedoBtnTarget) this.redoBtnTarget.disabled = !canRedo
  }

  // ── Required documents pop-up (Larry 2026-09-15) ──
  openDocs(e) { if (e) e.preventDefault(); this.docsPanelTarget.classList.remove("hidden"); document.body.style.overflow = "hidden" }
  closeDocs() { this.docsPanelTarget.classList.add("hidden"); document.body.style.overflow = "" }

  // ── Subcontractor documents in the send popup (Larry 2026-08-06) ────────
  // Each document is required / optional / not requested. The note underneath
  // says plainly whether this send goes straight out or waits on paperwork.
  docRequirementChanged() {
    this._refreshDocGateNote()
  }

  // "He's already given me his COI" (Larry 2026-08-11) — the GC files a
  // document he already holds straight from the send modal. Presign → S3
  // PUT → upload_document, the same path (and AI check) the Subcontractors
  // page uses; the row's status line updates in place.
  async docFilePicked(event) {
    const input = event.currentTarget
    const file = input.files?.[0]
    const docType = input.dataset.docType
    input.value = ""
    if (!file || !docType || !this.hasDocRequirementsTarget) return

    const wrap = this.docRequirementsTarget
    const vendorId = wrap.dataset.docUploadVendorId
    const row = wrap.querySelector(`[data-doc-row="${docType}"]`)
    const statusEl = row?.querySelector("[data-doc-status]")
    const labelEl = row?.querySelector("[data-doc-upload-label]")
    if (!vendorId || !row) return

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const say = (text, cls) => {
      if (!statusEl) return
      statusEl.textContent = text
      statusEl.className = `text-[12px] ${cls}`
    }

    try {
      if (labelEl) labelEl.textContent = "Uploading…"
      say("Uploading…", "text-blue-600")
      const presignResp = await fetch(wrap.dataset.docUploadPresignUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
        credentials: "same-origin",
        body: JSON.stringify({ id: vendorId, filename: file.name,
                               content_type: file.type || "application/octet-stream",
                               byte_size: file.size })
      })
      const presign = await presignResp.json().catch(() => ({}))
      if (!presignResp.ok || !presign.upload_url) throw new Error(presign.error || "Upload failed — try again.")

      const putResp = await fetch(presign.upload_url, {
        method: "PUT", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file
      })
      if (!putResp.ok) throw new Error("Upload failed — try again.")

      say("Checking the document…", "text-blue-600")
      const uploadResp = await fetch(wrap.dataset.docUploadUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
        credentials: "same-origin",
        body: JSON.stringify({ id: vendorId, doc_type: docType,
                               cache_name: presign.cache_name, filename: file.name })
      })
      const data = await uploadResp.json().catch(() => ({}))
      if (!uploadResp.ok) throw new Error(data.error || "Upload failed — try again.")

      if (data.ok) {
        say("On file", "text-green-700")
        row.querySelector("label")?.classList.add("hidden")
      } else {
        say((data.issues || []).join(" ") || "Not accepted — check the file.", "text-red-600")
        if (labelEl) labelEl.textContent = "Upload"
      }
    } catch (err) {
      say(err?.message || "Upload failed — try again.", "text-red-600")
      if (labelEl) labelEl.textContent = "Upload"
    }
  }

  _docRequirements() {
    if (!this.hasDocRequirementsTarget) return null
    const selections = {}
    this.docRequirementsTarget.querySelectorAll("select[data-doc-type]").forEach((select) => {
      selections[select.dataset.docType] = select.value
    })
    return selections
  }

  _refreshDocGateNote() {
    if (!this.hasDocGateNoteTarget || !this.hasDocRequirementsTarget) return

    let outstanding = 0
    this.docRequirementsTarget.querySelectorAll("select[data-doc-type]").forEach((select) => {
      const row = select.closest("div.flex")
      const onFile = row?.querySelector(".text-green-700") != null
      if (select.value === "required" && !onFile) outstanding += 1
    })

    this.docGateNoteTarget.textContent = outstanding === 0
      ? "Everything required is already on file — this goes straight out for signature."
      : `${outstanding} document${outstanding === 1 ? "" : "s"} will be requested before the contract is sent for signature.`
    this.docGateNoteTarget.className = outstanding === 0
      ? "text-[12px] text-green-700 mt-2.5"
      : "text-[12px] text-amber-700 mt-2.5"
  }

  confirmSend(e) {
    if (e) e.preventDefault()
    const gcSigns = this.hasGcSignsTarget ? this.gcSignsTarget.checked : true

    // The sender signs right here — validate the adopted signature first.
    let signedName = "", signatureKind = "typed", signatureData = null
    if (gcSigns) {
      signedName = this.hasSendSignNameTarget ? this.sendSignNameTarget.value.trim() : ""
      if (!signedName) {
        this._toast("Type your full name to sign.", "error")
        if (this.hasSendSignNameTarget) this.sendSignNameTarget.focus()
        return
      }
      signatureKind = this._sendKind || "typed"
      if (signatureKind === "drawn") {
        if (!this._sendPadDirty) { this._toast("Draw your signature, or switch to Type.", "error"); return }
        signatureData = this.sendPadTarget.toDataURL("image/png")
      }
    }

    // Signer rows edited inline in the modal ride along with the send.
    const signers = this.hasSendSignerRowsTarget
      ? Array.from(this.sendSignerRowsTarget.querySelectorAll("[data-signer-edit]")).map((row) => ({
          id: row.dataset.signerId || null,
          name: (row.querySelector("[data-name]")?.value || "").trim(),
          email: (row.querySelector("[data-email]")?.value || "").trim(),
          role: row.querySelector("[data-role]")?.value || "Signer"
        })).filter((s) => s.name)
      : null

    this.closeSend()
    clearTimeout(this._saveTimer)
    // Flush any pending text edit, then persist the status transition and reload
    // so the whole page (status pill, details, progress, locked document) is consistent.
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    Promise.resolve(this._saveBody())
      .then(() => fetch(this.sendUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", "Accept": "application/json" },
        body: JSON.stringify({ gc_signs: gcSigns, signed_name: signedName, signature_kind: signatureKind,
                               signature_data: signatureData, signers: signers,
                               doc_requirements: this._docRequirements(),
                               // Uploaded PDF: the spots placed on it go with the send.
                               ...(this._spots() ? { signature_fields: this._spots().fieldsForSave() } : {}) })
      }))
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        const n = data.emails_sent || 0
        sessionStorage.setItem("contractFlash",
          n > 0 ? `Sent for signature — secure link emailed to ${n} ${n === 1 ? "signer" : "signers"}` : "Sent for signature — no signer emails on file, use Copy link to share")
        window.location.reload()
      })
      .catch(() => this._toast("Couldn't send for signature — please try again.", "error"))
  }

  // ── Editable draft text (autosave) ──
  markDirty() {
    this._dirty = true
    if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Editing…"
    clearTimeout(this._saveTimer)
    this._saveTimer = setTimeout(() => this._saveBody(), 1000)
  }

  autoSaveBody() { clearTimeout(this._saveTimer); this._saveBody() }

  // The uploaded-PDF page (Sending → Upload) has no text to save — its
  // draft is the signature spots placed on the PDF.
  _spots() {
    const el = this.element.querySelector("[data-controller~='contract-pdf-spots']")
    return el ? this.application.getControllerForElementAndIdentifier(el, "contract-pdf-spots") : null
  }

  _saveBody() {
    if (!this.hasDocBodyTarget) {
      const spots = this._spots()
      return spots ? spots.flush() : Promise.resolve()
    }
    if (!this.hasSaveUrlValue) return Promise.resolve()
    const body = this._cleanBodyHtml()
    if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Saving…"
    this._templateStatus("saving")
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return fetch(this.saveUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", "Accept": "application/json" },
      body: JSON.stringify({ body })
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        this._dirty = false
        if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Saved ✓"
        // The response echoes the linked template when the edit was written
        // through to it; absent means this contract isn't linked to one.
        if (data && data.template) {
          this._showTemplateChip(data.template.name)
          this._templateStatus("saved")
        } else {
          this._templateStatus(null)
        }
      })
      .catch(() => {
        if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Couldn't save — retry"
        this._templateStatus("error")
      })
  }

  // Fired on pagehide / visibility-hidden (refresh, close, navigate, tab-switch).
  // If there's an unsaved edit, send it with keepalive so the request survives
  // the page going away — this is what prevents losing work on refresh.
  _flushOnHide(e) {
    if (e && e.type === "visibilitychange" && document.visibilityState !== "hidden") return
    if (!this._dirty || !this.hasDocBodyTarget || !this.hasSaveUrlValue) return
    this._dirty = false
    clearTimeout(this._saveTimer)
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      fetch(this.saveUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", "Accept": "application/json" },
        body: JSON.stringify({ body: this._cleanBodyHtml() }),
        keepalive: true
      }).catch(() => {})
    } catch (_) { /* ignore */ }
  }

  saveDraft(e) {
    if (e) e.preventDefault()
    clearTimeout(this._saveTimer)
    this._saveBody().then(() => this._toast("Draft saved"))
  }

  // Real reminder emails to everyone who hasn't signed yet.
  async remind(e) {
    if (e) e.preventDefault()
    try {
      const resp = await fetch(this.remindUrlValue, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        credentials: "same-origin"
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.ok) {
        this._toast(data.count > 0 ? `Reminder emailed to ${data.count} ${data.count === 1 ? "signer" : "signers"}` : "No pending signers with an email to remind")
      } else {
        this._toast(data.message || "Couldn't send reminders.", "error")
      }
    } catch (_) {
      this._toast("Couldn't send reminders.", "error")
    }
  }

  copySigningLink(e) {
    if (e) e.preventDefault()
    const url = e.currentTarget.dataset.signingUrl
    if (!url) return
    navigator.clipboard?.writeText(url)
      .then(() => this._toast("Signing link copied — share it only with that signer"))
      .catch(() => this._toast("Couldn't copy the link.", "error"))
  }

  // ── Details (value + penalty, drafts only) ──
  toggleDetailsEdit(e) {
    if (e) e.preventDefault()
    if (this.hasDetailsFormTarget) this.detailsFormTarget.classList.toggle("hidden")
  }

  async saveDetails(e) {
    if (e) e.preventDefault()
    try {
      const resp = await fetch(this.detailsUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        credentials: "same-origin",
        body: JSON.stringify({
          value: this.hasValueInputTarget ? this.valueInputTarget.value : undefined,
          penalty: this.hasPenaltyInputTarget ? this.penaltyInputTarget.value : undefined
        })
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.ok) {
        if (this.hasValueTextTarget) this.valueTextTarget.textContent = data.value || "—"
        if (this.hasPenaltyTextTarget) this.penaltyTextTarget.textContent = data.penalty || "—"
        this.toggleDetailsEdit()
        this._toast("Details updated")
      } else {
        this._toast(data.message || "Couldn't save the details.", "error")
      }
    } catch (_) {
      this._toast("Couldn't save the details.", "error")
    }
  }

  // Recall-to-draft confirmation (out-for-signature contracts).
  openRecall(e) { if (e) e.preventDefault(); if (this.hasRecallModalTarget) this.recallModalTarget.classList.remove("hidden") }
  closeRecall(e) { if (e) e.preventDefault(); if (this.hasRecallModalTarget) this.recallModalTarget.classList.add("hidden") }
  stopRecall(e) { if (e) e.stopPropagation() }
  confirmRecall(e) {
    this.closeRecall()
    this.reopen(e)
  }

  // Declined/sent → back to an editable draft (signatures void, links rotated).
  async reopen(e) {
    if (e) e.preventDefault()
    try {
      const resp = await fetch(this.reopenUrlValue, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        credentials: "same-origin"
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.ok) {
        sessionStorage.setItem("contractFlash", "Reopened as draft — you can edit and resend it")
        window.location.reload()
      } else {
        this._toast(data.message || "Couldn't reopen the contract.", "error")
      }
    } catch (_) {
      this._toast("Couldn't reopen the contract.", "error")
    }
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  toast(e) { if (e) e.preventDefault(); this._toast(e.currentTarget.dataset.toast || "Done") }

  // ── Change a signer's email on a sent contract (Keane K3-35, 2026-10-08) ──
  // Signers lock once the contract is out; this is the one thing that can
  // still change: the address a signing link goes to. A new link is emailed,
  // the old one dies.
  openSignerEmail(e) {
    e.preventDefault()
    if (!this.hasSignerEmailModalTarget) return
    const { signerId, signerName, signerEmail } = e.currentTarget.dataset
    this._signerEmailId = signerId
    this.signerEmailNameTarget.textContent = signerName || ""
    this.signerEmailInputTarget.value = signerEmail || ""
    this._signerEmailError(null)
    this.signerEmailModalTarget.classList.remove("hidden")
    this.signerEmailInputTarget.focus()
    this.signerEmailInputTarget.select()
  }

  closeSignerEmail(e) {
    if (e) e.preventDefault()
    if (this.hasSignerEmailModalTarget) this.signerEmailModalTarget.classList.add("hidden")
  }

  signerEmailBackdropClose(e) { if (e.target === e.currentTarget) this.closeSignerEmail() }

  stopPropagation(e) { e.stopPropagation() }

  saveSignerEmail(e) {
    if (e) e.preventDefault()
    if (this._savingSignerEmail || !this._signerEmailId) return
    const texts = this.signerEmailModalTarget.dataset
    const email = this.signerEmailInputTarget.value.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return this._signerEmailError(texts.invalidText)
    this._savingSignerEmail = true
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.updateSignerUrlValue, {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ signer_id: this._signerEmailId, email })
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || !data.ok) return this._signerEmailError(data.error === "email_invalid" ? texts.invalidText : texts.failedText)
        const signer = data.signer || {}
        this.signersValue = (this.signersValue || []).map((s) => (String(s.id) === String(signer.id) ? { ...s, ...signer } : s))
        // The row updates in place: its email line and the button's own copy of it.
        const row = this.element.querySelector(`[data-signer-row][data-signer-id="${signer.id}"]`)
        if (row) {
          const line = row.querySelector("[data-signer-email]")
          if (line) { line.textContent = signer.email || ""; line.classList.toggle("hidden", !signer.email) }
          const btn = row.querySelector("[data-action='contract-workspace#openSignerEmail']")
          if (btn) btn.dataset.signerEmail = signer.email || ""
          const link = row.querySelector("[data-signing-url]")
          if (link && signer.signing_url) link.dataset.signingUrl = signer.signing_url
        }
        this.closeSignerEmail()
        this._toast(data.emailed ? (texts.savedText || "").replace("%{email}", signer.email || email) : texts.savedHeldText)
      })
      .catch(() => this._signerEmailError(texts.failedText))
      .finally(() => { this._savingSignerEmail = false })
  }

  _signerEmailError(message) {
    if (!this.hasSignerEmailErrorTarget) return
    this.signerEmailErrorTarget.textContent = message || ""
    this.signerEmailErrorTarget.classList.toggle("hidden", !message)
  }

  // ── Add / edit signers (popup) ──
  openSignerModal(e) {
    if (e) e.preventDefault()
    if (!this.hasSignerModalTarget) return
    if (this.hasNotifyAllTarget) this.notifyAllTarget.checked = false
    const current = (this.signersValue && this.signersValue.length) ? this.signersValue : [{}]
    this._renderModalRows(current)
    this._ensureDnd()
    this.signerModalTarget.classList.remove("hidden")
    const first = this.signerRowsTarget.querySelector("[data-name]")
    if (first) first.focus()
  }

  closeSignerModal(e) { if (e) e.preventDefault(); if (this.hasSignerModalTarget) this.signerModalTarget.classList.add("hidden") }
  signerBackdropClose(e) { if (e.target === e.currentTarget) this.closeSignerModal() }

  addSignerRow(e) {
    if (e) e.preventDefault()
    const row = this._buildModalRow({})
    this.signerRowsTarget.appendChild(row)
    this._renumberModal()
    const input = row.querySelector("[data-name]")
    if (input) input.focus()
  }

  removeSignerRow(e) {
    if (e) e.preventDefault()
    const row = e.currentTarget.closest("[data-signer-edit]")
    if (row) row.remove()
    this._renumberModal()
  }

  saveSigners(e) {
    if (e) e.preventDefault()
    if (this._savingSigners) return
    const signers = Array.from(this.signerRowsTarget.querySelectorAll("[data-signer-edit]"))
      .map((row) => ({
        id: row.dataset.signerId || null,
        name: (row.querySelector("[data-name]")?.value || "").trim(),
        email: (row.querySelector("[data-email]")?.value || "").trim(),
        role: row.querySelector("[data-role]")?.value || "Signer"
      }))
      .filter((s) => s.name)
    const notify = this.hasNotifyAllTarget && this.notifyAllTarget.checked

    this._savingSigners = true
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.saveSignersUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", "Accept": "application/json" },
      body: JSON.stringify({ signers, notify })
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        if (!data.ok) return Promise.reject("error")
        this.signersValue = data.signers || []
        this._renderSignerPanel(this.signersValue)
        this.closeSignerModal()
        this._toast("Signers saved — signature invites go out when you send for signature")
      })
      .catch(() => this._toast("Couldn't save signers — please try again.", "error"))
      .finally(() => { this._savingSigners = false })
  }

  // Build one editable, draggable row inside the popup.
  _buildModalRow(s) {
    const wrap = document.createElement("div")
    wrap.setAttribute("data-signer-edit", "")
    if (s.id) wrap.setAttribute("data-signer-id", s.id)
    wrap.className = "border border-gray-200 rounded-lg p-2.5 bg-white flex items-start gap-2"
    const x = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>'
    const grip = '<svg viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><circle cx="7" cy="4" r="1.4"/><circle cx="7" cy="10" r="1.4"/><circle cx="7" cy="16" r="1.4"/><circle cx="13" cy="4" r="1.4"/><circle cx="13" cy="10" r="1.4"/><circle cx="13" cy="16" r="1.4"/></svg>'
    const inputCls = "border border-gray-300 rounded-lg px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand/30"
    wrap.innerHTML =
      `<div class="flex flex-col items-center gap-1 pt-1.5 shrink-0">` +
        `<span data-handle draggable="true" title="Drag to reorder" style="cursor:grab" class="text-gray-300 hover:text-gray-500">${grip}</span>` +
        `<span data-order class="w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-[11px] font-semibold flex items-center justify-center">1</span>` +
      `</div>` +
      `<div class="flex-1 min-w-0 space-y-2">` +
        `<div class="flex items-center gap-2">` +
          `<input data-name type="text" placeholder="Full name" class="flex-1 ${inputCls}">` +
          `<button data-action="contract-workspace#removeSignerRow" title="Remove signer" class="text-gray-300 hover:text-red-500 shrink-0">${x}</button>` +
        `</div>` +
        `<div class="flex items-center gap-2">` +
          `<input data-email type="email" placeholder="Email address" class="flex-1 ${inputCls}">` +
          `<select data-role class="w-40 ${inputCls} bg-white"></select>` +
        `</div>` +
      `</div>`
    wrap.querySelector("[data-name]").value = s.name || ""
    wrap.querySelector("[data-email]").value = s.email || ""
    const sel = wrap.querySelector("[data-role]")
    const roles = ["Contractor (GC)", "Owner", "Subcontractor", "Consultant", "Supplier", "Architect / Engineer", "Other"]
    if (s.role && !roles.includes(s.role)) roles.unshift(s.role)
    roles.forEach((r) => { const o = document.createElement("option"); o.textContent = r; sel.appendChild(o) })
    sel.value = s.role || "Contractor (GC)"
    this._wireRowDnd(wrap)
    return wrap
  }

  _renderModalRows(signers) {
    this.signerRowsTarget.innerHTML = ""
    signers.forEach((s) => this.signerRowsTarget.appendChild(this._buildModalRow(s)))
    this._renumberModal()
  }

  // ── Drag-to-reorder for the popup rows ──
  _wireRowDnd(wrap) {
    const handle = wrap.querySelector("[data-handle]")
    if (!handle) return
    handle.addEventListener("dragstart", (e) => {
      this._dragEl = wrap
      wrap.style.opacity = "0.4"
      wrap.setAttribute("data-dragging", "")
      e.dataTransfer.effectAllowed = "move"
      try { e.dataTransfer.setData("text/plain", "") } catch (_) { /* Safari */ }
      if (e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(wrap, 16, 16)
    })
    handle.addEventListener("dragend", () => {
      wrap.style.opacity = ""
      wrap.removeAttribute("data-dragging")
      this._dragEl = null
      this._renumberModal()
    })
  }

  _ensureDnd() {
    if (this._dndReady || !this.hasSignerRowsTarget) return
    this._dndReady = true
    this.signerRowsTarget.addEventListener("dragover", (e) => {
      if (!this._dragEl) return
      e.preventDefault()
      const after = this._dragAfterElement(e.clientY)
      if (after == null) this.signerRowsTarget.appendChild(this._dragEl)
      else this.signerRowsTarget.insertBefore(this._dragEl, after)
      this._renumberModal()
    })
  }

  _dragAfterElement(y) {
    const els = [...this.signerRowsTarget.querySelectorAll("[data-signer-edit]:not([data-dragging])")]
    return els.reduce((closest, child) => {
      const box = child.getBoundingClientRect()
      const offset = y - box.top - box.height / 2
      if (offset < 0 && offset > closest.offset) return { offset, element: child }
      return closest
    }, { offset: Number.NEGATIVE_INFINITY }).element || null
  }

  _renumberModal() {
    // Rows live in two places now: the Add/edit popup and the send modal.
    const containers = []
    if (this.hasSignerRowsTarget) containers.push(this.signerRowsTarget)
    if (this.hasSendSignerRowsTarget) containers.push(this.sendSignerRowsTarget)
    containers.forEach((c) => c.querySelectorAll("[data-signer-edit]").forEach((row, i) => {
      const badge = row.querySelector("[data-order]")
      if (badge) badge.textContent = String(i + 1)
    }))
  }

  // Re-render the read-only signers panel from the saved list.
  _renderSignerPanel(signers) {
    if (this.hasSignersListTarget) {
      this.signersListTarget.innerHTML = ""
      signers.forEach((s, i) => this.signersListTarget.appendChild(this._buildPanelRow(s, i)))
      this.signersListTarget.classList.toggle("hidden", signers.length === 0)
    }
    if (this.hasSignersEmptyTarget) this.signersEmptyTarget.classList.toggle("hidden", signers.length > 0)
    if (this.hasSignerCountTarget) {
      const signed = signers.filter((s) => s.signed_on).length
      this.signerCountTarget.textContent = signers.length > 0 ? `${signed} of ${signers.length} signed` : ""
    }
  }

  _buildPanelRow(s, i) {
    const row = document.createElement("div")
    row.className = "flex items-center gap-2.5"
    row.setAttribute("data-signer-row", "")
    const email = s.email ? `<div class="text-[11px] text-gray-400 truncate">${this._esc(s.email)}</div>` : ""
    const check = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="w-3.5 h-3.5"><path stroke-linecap="round" stroke-linejoin="round" d="m5 13 4 4L19 7"/></svg>'
    const status = s.signed_on
      ? `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-medium bg-green-50 text-green-700 shrink-0">${check} Signed</span>`
      : `<span class="text-[12px] text-gray-400 shrink-0">Awaiting send</span>`
    row.innerHTML =
      `<span data-order class="text-[12px] text-gray-400 font-medium w-3 text-right shrink-0">${i + 1}</span>` +
      `<div class="w-7 h-7 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-[11px] font-semibold shrink-0">${this._esc(s.initials)}</div>` +
      `<div class="min-w-0 flex-1"><div class="text-gray-800 truncate">${this._esc(s.name)} <span class="text-gray-400">· ${this._esc(s.role)}</span></div>${email}</div>` +
      status
    return row
  }

  _esc(s) {
    const d = document.createElement("div")
    d.textContent = s == null ? "" : String(s)
    return d.innerHTML
  }

  _showPendingFlash() {
    const msg = sessionStorage.getItem("contractFlash")
    if (!msg) return
    sessionStorage.removeItem("contractFlash")
    this._toast(msg)
  }

  // ── Persist the AI chat + review panels in this browser (keyed per contract)
  //    so they aren't wiped on refresh. ──
  _panelKey(name) {
    const base = this.hasAskUrlValue ? this.askUrlValue : window.location.pathname
    return `clContractWS:${name}:${base}`
  }

  _persistPanels() {
    try {
      if (this.hasChatMessagesTarget) localStorage.setItem(this._panelKey("chat"), this.chatMessagesTarget.innerHTML)
      if (this.hasReviewPaneTarget) localStorage.setItem(this._panelKey("review"), this.reviewPaneTarget.innerHTML)
      const countEl = this.element.querySelector('[data-contract-workspace-target~="issueCount"]')
      if (countEl) localStorage.setItem(this._panelKey("issues"), countEl.textContent.trim())
    } catch (_) { /* storage unavailable/full — ignore */ }
  }

  _restorePanels() {
    try {
      const chat = localStorage.getItem(this._panelKey("chat"))
      if (chat && this.hasChatMessagesTarget) {
        this.chatMessagesTarget.innerHTML = chat
        this.chatMessagesTarget.scrollTop = this.chatMessagesTarget.scrollHeight
      }
      const review = localStorage.getItem(this._panelKey("review"))
      if (review && this.hasReviewPaneTarget) this.reviewPaneTarget.innerHTML = review
      // Sync BOTH the Review tab badge and the in-pane "things to check" counter
      // to the saved number (the tab badge lives outside the restored pane).
      const issues = localStorage.getItem(this._panelKey("issues"))
      if (issues !== null && issues !== "") {
        this.element.querySelectorAll('[data-contract-workspace-target~="issueCount"]').forEach((el) => { el.textContent = issues })
      }
    } catch (_) { /* ignore */ }
  }

  _toast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const p = document.createElement("p")
    p.className = "flex-1 text-sm"
    p.textContent = message
    el.appendChild(p)
    document.body.prepend(el)
  }
}
